/**
 * ================================================================================================
 *  A PREFERENCE LIVES IN TWO PLACES, AND THIS FILE IS THE ONLY ONE THAT KNOWS BOTH.
 * ================================================================================================
 *
 * The Owner, 2026-10-29:
 *
 *     *"ההגדרות יישמרו ויופיעו נכון לאחר רענון וכניסה מחדש."*
 *
 *     *"אם שמירת העדפות בין מכשירים מחייבת הרחבת סכימה, פרט וממש במסגרת השלמת ההעדפות."*
 *
 * ── WHAT WAS WRONG BEFORE THIS FILE, STATED AS A FACT ───────────────────────────────────────────
 *
 * The home tiles have been ordered in `localStorage` since 2026-10-28. That satisfies *"לאחר
 * רענון"* and does NOT satisfy *"וכניסה מחדש"* in the sense that matters: browser storage is per
 * origin PER BROWSER PROFILE, so a person who arranges their screen at the office and opens the
 * product on a laptop gets the approved default, and nothing on the laptop could have known
 * otherwise. It is also the first thing a private window, a wiped machine or a site-data setting
 * clears.
 *
 * ── SO: THE ACCOUNT IS THE TRUTH ACROSS DEVICES, THE DEVICE IS THE COPY THAT ANSWERS AT ONCE ────
 *
 * Both are needed and neither is redundant:
 *
 *   · THE DEVICE IS WRITTEN SYNCHRONOUSLY, INSIDE THE DROP. A tile being dragged has to settle in
 *     the frame it was dropped in. A round trip in that path would make the layout wait on a
 *     network, and a failed request would have to either undo a move the person just made or leave
 *     the screen disagreeing with itself.
 *   · THE ACCOUNT IS WRITTEN RIGHT AFTER, AND BEST-EFFORT. If that push fails, the move still
 *     applies here and simply does not reach the other machine — which is exactly the behaviour
 *     this product had yesterday, so a failure is a return to the old state rather than a new
 *     defect.
 *
 * ── WHO WINS WHEN THE TWO DISAGREE: THE SERVER, ON LOAD ─────────────────────────────────────────
 *
 * `hydrate()` reads the account and overwrites the device copy. **NOT a timestamp comparison**: a
 * last-write-wins by clock needs a clock on the device, and a machine whose clock is wrong would
 * then win every exchange, silently and forever. Server-wins-on-load is wrong in exactly one case
 * — a reorder on one device while a second device already has the page open — and the cost there is
 * that the second device shows the first one's order on its next load, which is what *"בין
 * מכשירים"* means.
 *
 * ── AND THE ONE CASE THAT NEEDED DECIDING: THE SERVER HAS NOTHING ───────────────────────────────
 *
 * Then the device's own stored value is pushed UP, so somebody who arranged their screen before
 * this feature existed keeps that arrangement instead of losing it to the arrival of the feature.
 *
 * **ONLY A STORED VALUE IS PUSHED, NEVER A DEFAULT**, and the distinction is the whole safety of
 * it: a fresh machine has nothing stored, so it sends nothing, so it cannot overwrite the order
 * another machine saved. If two machines both have a stored order and the account has none, the
 * first to load wins — there is no information anywhere that could decide it better, and a
 * conservative wrong answer here costs one re-arrangement.
 */

import { PREFERENCE_KEYS, type PreferenceKey } from '../api/contract';
import { callOperation } from '../api/operations';

/**
 * EACH PREFERENCE AND THE KEY ITS COPY ON THIS MACHINE IS UNDER.
 *
 * The two namespaces are deliberately different. `glpoint.homeOrder.main` is what shipped on
 * 2026-10-28 and is **not renamed**: a rename would orphan the order every existing user has
 * already arranged, for no benefit but tidiness. `Record<PreferenceKey, string>` means a preference
 * added to the vocabulary without a device key does not compile.
 */
const DEVICE_KEY: Readonly<Record<PreferenceKey, string>> = {
  'home.order.main': 'glpoint.homeOrder.main',
  'home.order.side': 'glpoint.homeOrder.side',
  /* 2026-11-01: which cards this person chose NOT to show. See the service's note on why it is
     the exclusions that are stored and not the inclusions. */
  'home.hidden': 'glpoint.homeHidden',
  /*
   * `glpoint.favourites` IS NOT RENAMED, for the reason the two above are not: it is what
   * `favourites.ts` has been writing since the customer kit landed, and a rename would orphan
   * every star anybody has already set — for no benefit but tidiness.
   */
  'home.favourites': 'glpoint.favourites',
  'home.currencies': 'glpoint.currencies',
};

/**
 * WHERE A PREFERENCE'S COPY ON THIS MACHINE LIVES.
 *
 * Exported so the ONE other module that needs it — `favourites.ts`, whose cross-tab listener
 * compares the key a `storage` event carries — asks rather than restates. A second copy of this
 * mapping is how a rename here becomes a listener silently watching a key nothing writes.
 */
export function deviceKeyFor(key: PreferenceKey): string {
  return DEVICE_KEY[key];
}

/**
 * THE DEVICE COPY, OR `null` FOR *NOTHING SAVED HERE*.
 *
 * `null` and `[]` are different facts and the difference decides who wins in `hydrate()`: nothing
 * saved means this machine has no opinion and must not push one. Every access is wrapped, because
 * `localStorage` throws in a private window and with site data blocked, and the order of a few
 * panels must never be the reason a screen fails to render.
 */
export function readOnDevice(key: PreferenceKey): readonly string[] | null {
  try {
    const raw = typeof window === 'undefined' ? null : window.localStorage.getItem(DEVICE_KEY[key]);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((entry): entry is string => typeof entry === 'string');
  } catch {
    return null;
  }
}

function writeOnDevice(key: PreferenceKey, value: readonly string[]): void {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DEVICE_KEY[key], JSON.stringify(value));
    }
  } catch {
    /* The change still applies to this page; it simply will not survive a reload here. */
  }
}

/**
 * SAVE A PREFERENCE: THIS MACHINE NOW, THE ACCOUNT NEXT.
 *
 * The device write is synchronous and happens FIRST, so the caller may return the new value
 * immediately. The push is fire-and-forget by design, not by omission — see the header — and every
 * failure path is swallowed here rather than thrown at a drop handler:
 *
 *   · `callOperation` can throw SYNCHRONOUSLY where `fetch` is absent (a test environment, an old
 *     embedded browser), which a bare `.catch()` would not catch;
 *   · the request can be refused, offline or answered 4xx, and none of those is something to show
 *     somebody who just dragged a panel three inches.
 */
export function rememberPreference(key: PreferenceKey, value: readonly string[]): void {
  writeOnDevice(key, value);
  try {
    void callOperation('user.preference.update', { body: { key, value } }).catch(() => {
      /* The account keeps its previous answer. This machine keeps the new one. */
    });
  } catch {
    /* No transport at all. Same outcome, reached sooner. */
  }
}

/**
 * READ THE ACCOUNT AND RECONCILE THIS MACHINE WITH IT.
 *
 * Answers with the keys whose DEVICE COPY CHANGED, so a caller knows whether anything on screen
 * needs to be read again — and answers an empty list when the account agreed with the machine,
 * which is the common case and must not cause a re-render.
 *
 * A FAILED READ IS AN EMPTY ANSWER, NOT AN ERROR. The device copy stands, the screen renders, and
 * the product behaves as it did yesterday. There is no state in which a person cannot see their
 * home screen because a preference read did not come back.
 */
/**
 * ── ONE READ PER GESTURE, SHARED BY EVERY CALLER. **Found in the browser, 2026-11-01.** ───────
 *
 * THREE COMPONENTS CALL THIS ON MOUNT and two of them mount together on the home screen: the main
 * region in `TenantHomeScreen` and the rail in `MarketRail`. Each answers *"did anything change"*
 * by comparing the account against the DEVICE COPY — so the first caller wrote the device copy and
 * got `['home.order.side']`, and the second compared the account against a device copy that now
 * AGREED with it, got an empty list, and set no state.
 *
 * What that looked like on a second machine: the order was fetched, it was saved, and the screen
 * still showed the default — until the page was loaded a second time, when the device copy
 * answered. *"שמירה בין מכשירים"* appeared to work on every load except the one that matters.
 *
 * It was invisible to the suites because each case calls this once, and invisible in jsdom
 * besides. It showed up the first time a drag was performed with a mouse, the device copy cleared,
 * and the page reloaded.
 *
 * THE SHARE IS WHILE THE READ IS IN FLIGHT, AND NOT A CACHE. Callers that mount in the same tick
 * await the same promise and are told the same thing; a call made later — after the answer has
 * settled — reads again, because a cache that outlived the gesture would hide a change made on
 * another machine while this page was open. Every caller still re-reads the device copy itself,
 * which is where the account's answer was written.
 */
let inFlight: Promise<readonly PreferenceKey[]> | null = null;

export function hydratePreferences(): Promise<readonly PreferenceKey[]> {
  inFlight ??= readAndReconcile().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function readAndReconcile(): Promise<readonly PreferenceKey[]> {
  let answer: unknown;
  try {
    answer = await callOperation('user.preference.read', {});
  } catch {
    return [];
  }

  /*
   * ── THE ANSWER IS NARROWED, AND THE DECLARED RESPONSE TYPE IS NOT TAKEN AS A FACT ─────────────
   *
   * `OperationResponses` says this read returns `{ preferences: [...] }`, and a type is not a
   * control over what arrives — the Owner's rule about requests holds in this direction too.
   * **A test found this by failing**: a harness that answered a route it had no stub for returned
   * an object with no `preferences`, `for…of` threw inside a floating promise, and the rejection
   * surfaced in an unrelated suite. An estate behind a proxy that rewrites an error body would
   * reach the same line.
   *
   * There is nothing to report and nothing to retry: a preference read that did not come back means
   * the device copy stands, which is correct and is what this function answers with.
   */
  const preferences = (answer as { preferences?: unknown } | null)?.preferences;
  if (!Array.isArray(preferences)) return [];

  const onAccount = new Map<PreferenceKey, readonly string[]>();
  for (const entry of preferences as readonly { key?: unknown; value?: unknown }[]) {
    if (!isKnownKey(entry?.key)) continue;
    // The server validated the shape before storing it and again before returning it. This narrows
    // anyway: `value` is `unknown` on the wire, and a list of strings is what this file can use.
    if (!Array.isArray(entry.value)) continue;
    const list = entry.value.filter((item): item is string => typeof item === 'string');
    if (list.length !== entry.value.length) continue;
    onAccount.set(entry.key, list);
  }

  const changed: PreferenceKey[] = [];
  for (const key of PREFERENCE_KEYS) {
    const theirs = onAccount.get(key);
    const ours = readOnDevice(key);

    if (theirs === undefined) {
      /*
       * THE ACCOUNT HAS NO ANSWER. Push this machine's, but only if it HAS one: a fresh machine
       * with nothing stored must not overwrite an order another machine saved. See the header.
       */
      if (ours !== null) rememberPreference(key, ours);
      continue;
    }

    if (ours !== null && same(ours, theirs)) continue;
    writeOnDevice(key, theirs);
    changed.push(key);
  }
  return changed;
}

/** The vocabulary is closed on this side too, so a key the server invented cannot reach storage. */
function isKnownKey(key: unknown): key is PreferenceKey {
  return typeof key === 'string' && (PREFERENCE_KEYS as readonly string[]).includes(key);
}

function same(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((entry, at) => entry === b[at]);
}
